"use client";

import React, { useEffect, useState, useRef } from "react";
import { MagnifyingGlassIcon, TrashIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { cn } from "@/lib/utils";

export default function SearchBar() {
  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Mock data - replace with your actual data
  const [recentSearches, setRecentSearches] = useState(["Phone"]);
  const recommendedItems = ["Watch", "Computer", "Electric Cooker", "Gas Cylinder", "Kitchen Knife"];

  // Mock search results - replace with actual search logic
  const searchResults = searchQuery
    ? [
        `${searchQuery} 24 ultra`,
        `${searchQuery} galaxy s24 ultra`,
        `${searchQuery} s26 ultra original`,
        `${searchQuery} mobile phone`,
        `${searchQuery} s35 ultra`,
        `${searchQuery} s24 ultra case`,
      ]
    : [];

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && searchOpen) {
        setSearchOpen(false);
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [searchOpen]);

  // Lock body scroll when search is open
  useEffect(() => {
    if (searchOpen) {
      // Save current body overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      // Prevent scrolling
      document.body.style.overflow = "hidden";

      return () => {
        // Restore original overflow style
        document.body.style.overflow = originalStyle;
      };
    }
  }, [searchOpen]);

  const handleSearchFocus = () => {
    setSearchOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearRecentSearches = () => {
    setRecentSearches([]);
  };

  const handleSearchItemClick = (item: string) => {
    setSearchQuery(item);
    // Add to recent searches if not already there
    if (!recentSearches.includes(item)) {
      setRecentSearches([item, ...recentSearches]);
    }
    // Navigate or perform search here
  };

  return (
    <div ref={searchContainerRef} className='relative w-full'>
      <InputGroup
        className={cn(
          "border-border-title has-[[data-slot=input-group-control]:focus-visible]:border-border-title h-full rounded-[12px] border-none has-[[data-slot=input-group-control]:focus-visible]:ring-[0px]",
          searchOpen && "invisible"
        )}
      >
        <InputGroupInput
          ref={searchInputRef}
          placeholder='Search for products, brands, and categories...'
          onFocus={handleSearchFocus}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <InputGroupAddon
          className='mr-2 h-full rounded-[12px] bg-[#FFB900] p-3 md:mr-0 md:rounded-none md:rounded-r-[12px] md:px-3 md:py-4'
          align='inline-end'
        >
          <MagnifyingGlassIcon className='text-icon-title-inv size-4 cursor-pointer stroke-[2px]' title='Search' />
        </InputGroupAddon>
      </InputGroup>

      {/* Search Dropdown */}
      {searchOpen && (
        <>
          {/* Overlay */}
          <div
            className='animate-in fade-in-0 fixed inset-0 z-40 h-svh w-screen overflow-hidden bg-black/50 duration-200'
            onClick={() => setSearchOpen(false)}
          />

          {/* Replica Input - Floating above overlay */}
          <div className='animate-in fade-in-0 absolute top-0 left-0 z-50 w-full duration-200'>
            <InputGroup className='border-border-title bg-bg-base has-[[data-slot=input-group-control]:focus-visible]:border-border-title h-full rounded-[12px] border-[1.5px] has-[[data-slot=input-group-control]:focus-visible]:ring-[0px]'>
              <InputGroupInput
                placeholder='Search for products, brands, and categories...'
                value={searchQuery}
                onChange={handleSearchChange}
                autoFocus
              />
              <InputGroupAddon
                align='inline-end'
                className='mr-2 h-full rounded-[12px] bg-[#FFB900] p-3 md:mr-0 md:rounded-r-[12px] md:px-3 md:py-4'
              >
                <MagnifyingGlassIcon
                  className='text-icon-title-inv size-4 cursor-pointer stroke-[2px]'
                  title='Search'
                />
              </InputGroupAddon>
            </InputGroup>
          </div>

          {/* Search Results Popup */}
          <div className='bg-bg-base scrollable border-border-title animate-in fade-in-0 slide-in-from-top-2 absolute top-full left-0 z-[60] mt-2 w-full rounded-[12px] border shadow-lg duration-200'>
            {searchQuery ? (
              /* Search Results */
              <div className='max-h-[400px] overflow-x-hidden overflow-y-auto p-4'>
                <div className='space-y-1'>
                  {searchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearchItemClick(result)}
                      className='text-text-muted hover:bg-bg-tetiary hover:text-text-title w-full rounded-lg px-4 py-3 text-left text-sm break-words whitespace-normal transition-colors'
                    >
                      {`${result}`}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Recent Searches & Recommendations */
              <div className='max-h-[400px] overflow-y-auto p-4'>
                {recentSearches.length > 0 && (
                  <div className='mb-6'>
                    <div className='mb-3 flex items-center justify-between'>
                      <h3 className='text-text-title text-sm font-semibold'>Recent Search</h3>
                      <button
                        onClick={handleClearRecentSearches}
                        className='text-icon-muted hover:text-icon-title transition-colors'
                        title='Clear recent searches'
                      >
                        <TrashIcon className='size-4' />
                      </button>
                    </div>
                    <div className='space-y-1'>
                      {recentSearches.map((search, index) => (
                        <button
                          key={index}
                          onClick={() => handleSearchItemClick(search)}
                          className='text-text-muted hover:bg-bg-tetiary hover:text-text-title w-full rounded-lg px-4 py-2 text-left text-sm break-words whitespace-normal transition-colors'
                        >
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className='mb-3 flex items-center justify-between'>
                    <h3 className='text-text-title text-sm font-semibold'>Recommended for you</h3>
                    <button
                      className='text-icon-muted hover:text-icon-title transition-colors'
                      title='Refresh recommendations'
                    >
                      <ArrowPathIcon className='size-4' />
                    </button>
                  </div>
                  <div className='space-y-1'>
                    {recommendedItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleSearchItemClick(item)}
                        className='text-text-muted hover:bg-bg-tetiary hover:text-text-title w-full rounded-lg px-4 py-2 text-left text-sm break-words whitespace-normal transition-colors'
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
